<script setup lang="ts">
const documentId = inject("documentId") as any;

const isExportingWord = ref(false);
const isExportingPDF = ref(false);
const isPrinting = ref(false);
const isExportingGoogleDrive = ref(false);

const documentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(documentStore);

const exportToWord = async () => {
  isExportingWord.value = true;
  try {
    await useExportCompanionDocumentFromOps(
      documentId,
      ExportDocumentFormat.docx,
      false,
      false,
      true,
      submittedDocument.value?.name,
      true
    );
  } finally {
    isExportingWord.value = false;
  }
};

const exportToPDF = async () => {
  isExportingPDF.value = true;
  try {
    await useExportCompanionDocumentFromOps(
      documentId,
      ExportDocumentFormat.pdf,
      false,
      false,
      true,
      submittedDocument.value?.name,
      true
    );
  } finally {
    isExportingPDF.value = false;
  }
};

const printDocument = async () => {
  isPrinting.value = true;
  try {
    await useExportCompanionDocumentFromOps(
      documentId,
      ExportDocumentFormat.pdf,
      false,
      true,
      true,
      submittedDocument.value?.name,
      true
    );
  } finally {
    isPrinting.value = false;
  }
};

const exportToGoogleDrive = async () => {
  if (isExportingGoogleDrive.value) return;

  try {
    await useExportCompanionDocumentToGoogleDrive(documentId, {
      onProcessingStateChange: (isProcessing) => {
        isExportingGoogleDrive.value = isProcessing;
      },
    });
  } finally {
    isExportingGoogleDrive.value = false;
  }
};
</script>

<template>
  <div class="w-full flex flex-col items-center transition-all duration-200 mb-4">
    <div v-if="$slots.default" class="w-full flex justify-center mb-2">
      <slot />
    </div>

    <div class="flex justify-center items-center w-full">
      <BaseTextButton
        @click.stop.prevent="exportToWord"
        :show-spinner="isExportingWord"
        color="default"
        class="!px-3"
      >
        <div class="flex flex-row items-center justify-center">
          <icon name="file-icons:microsoft-word" class="mr-2" />
          <span class="block text-sm"> Word </span>
        </div>
      </BaseTextButton>

      <div class="h-5 w-[1px] bg-gray-300 mx-1"></div>

      <BaseTextButton
        @click.stop.prevent="exportToPDF"
        :show-spinner="isExportingPDF"
        color="default"
        class="!px-3"
      >
        <div class="flex flex-row items-center justify-center">
          <icon
            name="grommet-icons:document-pdf"
            class="mr-2 text-[#FF0000]"
          />
          <span class="block text-sm"> PDF </span>
        </div>
      </BaseTextButton>

      <div class="h-5 w-[1px] bg-gray-300 mx-1"></div>

      <BaseTextButton
        @click.stop.prevent="exportToGoogleDrive"
        :show-spinner="isExportingGoogleDrive"
        color="default"
        class="!px-3 transition-opacity"
        :class="{
          'opacity-60 pointer-events-none': isExportingGoogleDrive,
        }"
      >
        <div class="flex flex-row items-center justify-center">
          <icon name="logos:google-drive" class="mr-2" />
          <span class="block text-sm"> Google Drive </span>
        </div>
      </BaseTextButton>

      <div class="h-5 w-[1px] bg-gray-300 mx-1"></div>

      <BaseTextButton
        @click.stop.prevent="printDocument"
        :show-spinner="isPrinting"
        color="default"
        class="!px-3"
      >
        <div class="flex flex-row items-center justify-center">
          <icon name="material-symbols:print" class="mr-2" />
          <span class="block text-sm"> Print </span>
        </div>
      </BaseTextButton>
    </div>
  </div>
</template>

<style scoped></style>
